import { ComponentType, memo } from 'react'

import { useOrderDetails } from 'src/contexts/OrderDetailsContext'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useVerbitConnectAvailability } from 'src/hooks/useVerbitConnectAvailability'
import { useCurrentTimeTick } from 'src/hooks/useCurrentTimeTick'

import * as S from './styles'
import { EmptyTranscriptTemplate, getTemplateType } from './utils'
import {
    SessionScheduledTemplate,
    WaitingForTranscriptTemplate,
    WaitingForDocumentTemplate,
    SettingUpSessionTemplate,
    ReadyToConnectTemplate,
    SessionEndedTemplate,
} from './templates'

const templatesMap: Record<EmptyTranscriptTemplate, ComponentType> = {
    [EmptyTranscriptTemplate.sessionScheduled]: SessionScheduledTemplate,
    [EmptyTranscriptTemplate.waitingForTranscript]: WaitingForTranscriptTemplate,
    [EmptyTranscriptTemplate.waitingForDocument]: WaitingForDocumentTemplate,
    [EmptyTranscriptTemplate.settingUpSession]: SettingUpSessionTemplate,
    [EmptyTranscriptTemplate.readyToConnect]: ReadyToConnectTemplate,
    [EmptyTranscriptTemplate.sessionEnded]: SessionEndedTemplate,
}

// since `EmptyTranscriptScreen` is rerendered every second due to `useCurrentTimeTick`,
// templates' inner state is also reset (for example, in `SettingUpSessionTemplate`,
// loading animation is constantly reset instead of playing continuously);
// to prevent this, we use the `memo` wrapper
const TemplateRenderer = memo(({ templateType }: { templateType: EmptyTranscriptTemplate }) => {
    const Template = templatesMap[templateType]

    return <Template />
})

export const EmptyTranscriptScreen = () => {
    const metadata = useTranscriptMetadata()
    const { order, isLoading } = useOrderDetails()
    const { isMediaRecorderAvailable } = useVerbitConnectAvailability()
    const currentTime = useCurrentTimeTick()

    const templateType = getTemplateType({
        viewMode: metadata?.viewMode ?? null,
        order,
        isOrderLoading: isLoading,
        isMediaRecorderAvailable,
        currentTime,
    })

    if (templateType === null) {
        return null
    }

    return (
        <S.Wrapper>
            <TemplateRenderer templateType={templateType} />
        </S.Wrapper>
    )
}
